import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Activity, User, ArrowRight, Plus, Trash2, Mail, MessageSquare, ChevronDown, Phone, PhoneMissed } from 'lucide-react';
import api from '../services/api';

// Per-event glyph + tone — keeps the timeline scannable without a legend.
const TYPE_META = {
  lead_created:          { Icon: Plus,           tone: 'text-green-600' },
  lead_updated:          { Icon: Activity,       tone: 'text-gray-500' },
  lead_field_changed:    { Icon: ArrowRight,     tone: 'text-blue-600' },
  lead_status_updated:   { Icon: ArrowRight,     tone: 'text-blue-600' },
  lead_priority_updated: { Icon: ArrowRight,     tone: 'text-amber-600' },
  lead_stage_changed:    { Icon: ArrowRight,     tone: 'text-indigo-600' },
  lead_assigned:         { Icon: User,           tone: 'text-purple-600' },
  status_changed:        { Icon: ArrowRight,     tone: 'text-blue-600' },
  note_added:            { Icon: MessageSquare,  tone: 'text-gray-500' },
  note_deleted:          { Icon: Trash2,         tone: 'text-red-500' },
  email_sent:            { Icon: Mail,           tone: 'text-purple-500' },
  sms_sent:              { Icon: MessageSquare,  tone: 'text-teal-500' },
  call_logged:           { Icon: Phone,          tone: 'text-green-600' },
  missed_call:           { Icon: PhoneMissed,    tone: 'text-red-500' },
  lead_deleted:          { Icon: Trash2,         tone: 'text-red-600' },
};

// Map raw field names to readable labels.
const FIELD_LABELS = {
  status:     'Status',
  stageId:    'Stage',
  assignedTo: 'Owner',
  priority:   'Priority',
  firstName:  'First name',
  lastName:   'Last name',
  email:      'Email',
  phone:      'Phone',
  company:    'Company',
};

const formatTime = (d) => {
  if (!d) return '';
  const date = new Date(d);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  if (diffMs < 60_000) return 'just now';
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`;
  if (diffMs < 7 * 86_400_000) return `${Math.floor(diffMs / 86_400_000)}d ago`;
  return date.toLocaleDateString();
};

const LeadActivityTimeline = ({ leadId }) => {
  // Cursor-paginated fetch: load 30 newest, "Load older" button asks the
  // server for events older than the oldest one already loaded. Avoids
  // dragging a thousand-event history into the browser on first paint.
  const [pages, setPages] = useState([]); // arrays of activity batches
  const [loadingMore, setLoadingMore] = useState(false);

  const { data, isLoading, error, refetch } = useQuery(
    ['lead-activity', leadId, 'first'],
    () => api.get(`/activities/lead/${leadId}`).then((r) => r.data),
    {
      enabled: !!leadId,
      staleTime: 15 * 1000,
      onSuccess: (resp) => setPages([resp?.activities || []]),
    }
  );

  const loadOlder = async () => {
    const flat = pages.flat();
    const oldest = flat[flat.length - 1];
    if (!oldest) return;
    setLoadingMore(true);
    try {
      const resp = await api.get(`/activities/lead/${leadId}`, {
        params: { before: oldest.createdAt },
      });
      setPages((prev) => [...prev, resp.data?.activities || []]);
    } finally {
      setLoadingMore(false);
    }
  };

  const items = React.useMemo(() => pages.flat(), [pages]);
  const hasMore = !!data?.hasMore || pages.length > 1; // approximate; true if any page returned hasMore
  const lastBatch = pages[pages.length - 1];
  const lastBatchHasMore = lastBatch && lastBatch.length > 0
    ? (pages.length === 1 ? data?.hasMore : true)
    : false;

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <Activity size={20} className="mr-2" />
          Activity
          {items.length > 0 && <span className="ml-2 text-xs text-gray-500">({items.length})</span>}
        </h3>
      </div>
      <div className="card-body">
        {isLoading ? (
          <p className="text-gray-400 text-sm">Loading…</p>
        ) : error ? (
          <p className="text-red-500 text-sm">Could not load activity log.</p>
        ) : items.length === 0 ? (
          <p className="text-gray-400 text-sm italic">No activity recorded yet for this lead.</p>
        ) : (
          // Cap the timeline height so a long history doesn't push every
          // card below it off the visible viewport. We also paginate via
          // "Load older" so the initial fetch is bounded to 30 events.
          //
          // Layout: flex row per item, icon as the first column. Earlier
          // version positioned the icon absolutely at -left-9px so it
          // sat *on* the timeline line — that broke once we added
          // overflow-y-auto, since browsers force overflow-x: hidden in
          // tandem and clipped the icons. Flow-positioned icons stay
          // visible regardless of the overflow box.
          <ol className="space-y-4 max-h-[480px] overflow-y-auto pr-2">
            {items.map((a) => {
              const meta = TYPE_META[a.type] || { Icon: Activity, tone: 'text-gray-500' };
              const { Icon } = meta;
              const userName = a.userId?.name || a.userId?.email || 'System';
              const fieldLabel = a.field ? (FIELD_LABELS[a.field] || a.field) : null;
              return (
                <li key={a._id} className="flex gap-3">
                  <span className={`flex-shrink-0 w-6 h-6 mt-0.5 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center ${meta.tone}`}>
                    <Icon size={11} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-500">
                      <span className="font-medium text-gray-700">{userName}</span>
                      <span className="mx-1">·</span>
                      <span title={new Date(a.createdAt).toLocaleString()}>{formatTime(a.createdAt)}</span>
                    </div>
                    <div className="text-sm text-gray-900 mt-0.5">
                      {a.type === 'lead_field_changed' && fieldLabel ? (
                        <span>
                          <span className="font-medium">{fieldLabel}</span>
                          {' '}changed from{' '}
                          <span className="font-mono text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded">
                            {a.before || '∅'}
                          </span>
                          {' '}to{' '}
                          <span className="font-mono text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded">
                            {a.after || '∅'}
                          </span>
                        </span>
                      ) : (
                        <span>{a.description}</span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
            {lastBatchHasMore && (
              <li className="pt-2">
                <button
                  type="button"
                  onClick={loadOlder}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 disabled:opacity-50"
                >
                  <ChevronDown size={12} />
                  {loadingMore ? 'Loading…' : 'Load older entries'}
                </button>
              </li>
            )}
          </ol>
        )}
      </div>
    </div>
  );
};

export default LeadActivityTimeline;
